import React from "react";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import accounting from "accounting";
import dayjs from "dayjs";
import styles from "src/styles";

// Helpers
import { getClassName } from "src/helpers/common.helper";

type Props = Promotion & {
    onClick?: () => void;
};

const PromotionCard = ({
    imageUrl,
    name,
    isNew,
    endAt,
    price,
    priceType,
    onClick = () => {},
}: Props) => {
    return (
        <DivStyled
            className="promotion-card"
            onClick={onClick}
            imageUrl={imageUrl}
        >
            <div className="card">
                <div className="container">
                    <div className="content">
                        {isNew && (
                            <div className="header">
                                <div className="tag">
                                    <span>
                                        <FormattedMessage id="promotionCard.newItem" />
                                    </span>
                                </div>
                            </div>
                        )}
                        <div
                            className={getClassName(
                                "body",
                                isNew ? "with-header" : ""
                            )}
                        >
                            <div className="title">{name}</div>

                            {price !== undefined && (
                                <>
                                    <div className="price-start">
                                        <FormattedMessage id="discount" />
                                    </div>
                                    <div className="price">
                                        <FormattedMessage
                                            id="promotionCard.xBaht"
                                            values={{
                                                value: accounting.formatNumber(
                                                    price
                                                ),
                                            }}
                                        />
                                    </div>
                                </>
                            )}

                            {endAt !== undefined && (
                                <div className="end-at">
                                    <FormattedMessage
                                        id="promotionCard.endAtX"
                                        values={{
                                            value: dayjs(endAt).format(
                                                "DD MMMM YY"
                                            ),
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </DivStyled>
    );
};

const DivStyled = styled.div<{ imageUrl: string }>`
    &.promotion-card {
        width: 320px;
        height: 412px;

        .card {
            display: flex;
            justify-content: center;
            cursor: pointer;
            background: url(${(props) => props.imageUrl});
            background-repeat: no-repeat;
            background-size: 100%;
            border-radius: 12px;
            min-height: 368px;
        }

        .container {
            width: 100%;
            background: linear-gradient(
                180deg,
                rgba(1, 26, 64, 0) 27.08%,
                #3c69ac 80.41%
            );
            border-radius: 10px;
            transition: padding 0.3s;

            &:hover {
                padding-bottom: 10px;
            }
        }

        .header {
            & > .tag {
                width: 78px;
                height: 32px;
                background: #1cb197;
                display: flex;
                justify-content: flex-end;
                align-items: center;
                border-top-right-radius: 4px;
                border-bottom-right-radius: 4px;
                padding-right: 9px;
                margin-top: 20px;

                & > span {
                    font-style: normal;
                    font-weight: 400;
                    font-size: 18px;
                    line-height: 1.6;
                    color: white;

                    @media screen and (max-width: 767px) {
                        font-size: 14px;
                    }
                }
            }
        }

        .body {
            color: ${styles.fontColorWhite};
            padding: 0 32px 12px 32px;
            padding-top: 65%;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: space-between;
            line-height: 1.6;

            &.with-header {
                padding-top: calc(65% - 52px);
            }

            & > .title {
                font-style: normal;
                font-weight: 600;
                font-size: 24px;

                @media screen and (max-width: 767px) {
                    font-size: 20px;
                }
            }

            & > .price-start {
                font-style: normal;
                font-weight: 500;
                font-size: 22px;

                @media screen and (max-width: 767px) {
                    font-size: 18px;
                }
            }

            & > .price {
                font-style: normal;
                font-weight: 500;
                font-size: 36px;

                @media screen and (max-width: 767px) {
                    font-size: 24px;
                }

                .price-type {
                    margin-left: 4px;
                    font-style: normal;
                    font-weight: 400;
                    font-size: 16px;
                    color: ${styles.fontColorLightGray};
                }
            }

            & > .end-at {
                font-style: normal;
                font-weight: 400;
                font-size: 16px;

                @media screen and (max-width: 767px) {
                    font-size: 14px;
                }
            }
        }
    }
`;

export default PromotionCard;
